(() => {
  const sameOptions = (left = [], right = []) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index]);

  const refreshIonCartCount = async () => {
    try {
      const response = await fetch(`${window.routes?.cart_url || "/cart"}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll("[data-ion-cart-count]").forEach((element) => {
        element.textContent =
          cart.item_count > 0 ? String(cart.item_count) : "";
        element.dataset.count = String(cart.item_count || 0);
      });
    } catch (error) {
      console.warn("[ION product] Cart count could not be refreshed.", error);
    }
  };

  class IonProductPage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.variants = JSON.parse(
          this.querySelector("[data-ion-product-variants]")?.textContent ||
            "[]",
        );
      } catch (error) {
        console.error("[ION product] Variant data is invalid.", error);
        this.variants = [];
      }

      this.variantInput = this.querySelector("[data-ion-variant-input]");
      this.form = this.variantInput?.closest("form");
      this.addButton = this.querySelector("[data-ion-add]");
      this.addButtonText = this.querySelector("[data-ion-add-text]");
      this.feedback = this.querySelector("[data-ion-feedback]");
      this.inventory = this.querySelector("[data-ion-inventory]");
      this.price = this.querySelector("[data-ion-price]");
      this.comparePrice = this.querySelector("[data-ion-compare-price]");
      this.stickyBar = this.querySelector("[data-ion-sticky-bar]");
      this.stickyButton = this.querySelector("[data-ion-sticky-add]");
      this.stickyPrice = this.querySelector("[data-ion-sticky-price]");
      this.quantityInput = this.querySelector("[data-ion-quantity-input]");
      this.formQuantity = this.querySelector("[data-ion-form-quantity]");
      this.mediaCounter = this.querySelector("[data-ion-media-counter]");
      this.scarcityThreshold = Number(this.dataset.scarcityThreshold || 20);
      this.addLabel = this.dataset.addLabel || "Add to cart";
      this.soldOutLabel = this.dataset.soldOutLabel || "Sold out";

      const initialId = String(this.variantInput?.value || "");
      this.currentVariant =
        this.variants.find((variant) => String(variant.id) === initialId) ||
        this.variants[0];
      this.selectedOptions = [...(this.currentVariant?.options || [])];

      this.querySelectorAll("[data-ion-option-value]").forEach((button) => {
        button.addEventListener("click", () => {
          this.selectOption(
            Number(button.dataset.optionIndex),
            button.dataset.optionValue,
          );
        });
      });

      this.querySelectorAll("[data-ion-thumbnail]").forEach((button) => {
        button.addEventListener("click", () =>
          this.showMedia(button.dataset.mediaId),
        );
      });

      this.querySelector("[data-ion-quantity-minus]")?.addEventListener(
        "click",
        () => this.changeQuantity(-1),
      );
      this.querySelector("[data-ion-quantity-plus]")?.addEventListener(
        "click",
        () => this.changeQuantity(1),
      );
      this.quantityInput?.addEventListener("change", () =>
        this.setQuantity(this.quantityInput.value),
      );
      this.stickyButton?.addEventListener("click", () =>
        this.form?.requestSubmit(),
      );
      this.form?.addEventListener("submit", () => this.showAddingState());

      this.bindCartEvents();
      this.setupStickyBar();
      this.syncVariant({ updateUrl: false });
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscribe?.();
      this.cartErrorUnsubscribe?.();
      this.stickyObserver?.disconnect();
      window.clearTimeout(this.feedbackTimer);
    }

    selectOption(index, value) {
      const requested = [...this.selectedOptions];
      requested[index] = value;

      let variant = this.variants.find((item) =>
        sameOptions(item.options, requested),
      );
      if (!variant?.available) {
        variant =
          this.variants.find(
            (item) => item.available && item.options?.[index] === value,
          ) || variant;
      }
      if (!variant) return;

      this.currentVariant = variant;
      this.selectedOptions = [...variant.options];
      this.syncVariant({ updateUrl: true });
    }

    syncVariant({ updateUrl }) {
      const variant = this.currentVariant;
      if (!variant) return;

      this.variantInput.value = variant.id;
      this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));

      this.querySelectorAll("[data-ion-option-value]").forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        const value = button.dataset.optionValue;
        const selected = this.selectedOptions[index] === value;
        const candidate = [...this.selectedOptions];
        candidate[index] = value;
        const candidateVariant = this.variants.find((item) =>
          sameOptions(item.options, candidate),
        );

        button.setAttribute("aria-pressed", String(selected));
        button.disabled = !selected && !candidateVariant?.available;
      });

      this.selectedOptions.forEach((value, index) => {
        const label = this.querySelector(`[data-ion-option-label="${index}"]`);
        if (label) label.textContent = value;
      });

      if (this.price) this.price.textContent = variant.priceFormatted;
      if (this.stickyPrice)
        this.stickyPrice.textContent = variant.priceFormatted;

      const onSale =
        Number(variant.compareAtPrice || 0) > Number(variant.price || 0);
      if (this.comparePrice) {
        this.comparePrice.textContent = onSale
          ? variant.compareAtFormatted || ""
          : "";
        this.comparePrice.classList.toggle("is-hidden", !onSale);
      }

      this.updateInventory(variant);
      this.updatePurchaseButtons(variant);
      if (variant.mediaId) this.showMedia(variant.mediaId);

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
      }

      this.dispatchEvent(
        new CustomEvent("ion:variant-change", {
          bubbles: true,
          detail: { variant },
        }),
      );
    }

    updateInventory(variant) {
      if (!this.inventory) return;

      if (!variant.available) {
        this.inventory.textContent = this.soldOutLabel;
        this.inventory.dataset.state = "sold-out";
        return;
      }

      const tracked = Boolean(variant.inventoryManagement);
      const remaining = Math.max(0, Number(variant.inventoryQuantity || 0));
      if (tracked && remaining > 0 && remaining <= this.scarcityThreshold) {
        this.inventory.textContent = `Only ${remaining} left`;
        this.inventory.dataset.state = "low";
        return;
      }

      this.inventory.textContent = "Available";
      this.inventory.dataset.state = "available";
    }

    updatePurchaseButtons(variant) {
      const label = variant.available
        ? `${this.addLabel} — ${variant.priceFormatted}`
        : this.soldOutLabel;

      if (this.addButtonText) this.addButtonText.textContent = label;
      if (this.addButton) {
        if (variant.available) this.addButton.removeAttribute("aria-disabled");
        else this.addButton.setAttribute("aria-disabled", "true");
      }

      if (this.stickyButton) {
        this.stickyButton.textContent = variant.available
          ? this.addLabel
          : this.soldOutLabel;
        this.stickyButton.disabled = !variant.available;
      }
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      const id = String(mediaId);
      const target = [...this.querySelectorAll("[data-ion-media]")].find(
        (media) => String(media.dataset.mediaId) === id,
      );
      if (!target) return;

      this.querySelectorAll("[data-ion-media]").forEach((media) => {
        const active = media === target;
        media.hidden = !active;
        media.classList.toggle("is-active", active);
        if (!active) media.querySelector("video")?.pause();
      });

      this.querySelectorAll("[data-ion-thumbnail]").forEach((button) => {
        const active = String(button.dataset.mediaId) === id;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
        if (active)
          button.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          });
      });

      if (this.mediaCounter) {
        const position = Number(target.dataset.mediaPosition || 1);
        const total = this.querySelectorAll("[data-ion-media]").length;
        this.mediaCounter.textContent = `${String(position).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
      }
    }

    changeQuantity(change) {
      this.setQuantity(Number(this.quantityInput?.value || 1) + change);
    }

    setQuantity(value) {
      const quantity = Math.max(1, Math.floor(Number(value) || 1));
      if (this.quantityInput) this.quantityInput.value = quantity;
      if (this.formQuantity) this.formQuantity.value = quantity;
    }

    showAddingState() {
      if (!this.currentVariant?.available || !this.feedback) return;
      this.feedback.dataset.state = "loading";
      this.feedback.textContent = "Adding your selection…";
    }

    bindCartEvents() {
      if (
        typeof subscribe !== "function" ||
        typeof PUB_SUB_EVENTS === "undefined"
      )
        return;

      this.cartUpdateUnsubscribe = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (event?.source !== "product-form") return;
          if (
            String(event.productVariantId) !== String(this.currentVariant?.id)
          )
            return;

          this.feedback.dataset.state = "success";
          this.feedback.textContent = "Added to cart — your gear is locked in.";
          if (this.addButtonText)
            this.addButtonText.textContent = "Added to cart ✓";
          refreshIonCartCount();

          window.clearTimeout(this.feedbackTimer);
          this.feedbackTimer = window.setTimeout(() => {
            this.feedback.textContent = "";
            if (this.addButtonText && this.currentVariant) {
              this.addButtonText.textContent = `${this.addLabel} — ${this.currentVariant.priceFormatted}`;
            }
          }, 2600);
        },
      );

      this.cartErrorUnsubscribe = subscribe(
        PUB_SUB_EVENTS.cartError,
        (event) => {
          if (
            String(event.productVariantId) !== String(this.currentVariant?.id)
          )
            return;
          this.feedback.dataset.state = "error";
          this.feedback.textContent =
            event.errors ||
            event.message ||
            "That selection could not be added.";
        },
      );
    }

    setupStickyBar() {
      if (this.dataset.showSticky !== "true" || !this.stickyBar || !this.form)
        return;
      const purchaseZone = this.querySelector(".ion-product__purchase-zone");
      if (!purchaseZone || !("IntersectionObserver" in window)) return;

      this.stickyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        const passedForm =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.stickyBar.dataset.visible = String(passedForm);
        this.stickyBar.setAttribute("aria-hidden", String(!passedForm));
      });
      this.stickyObserver.observe(purchaseZone);
    }
  }

  class IonProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      this.loaded = true;
      this.loadRecommendations();
    }

    async loadRecommendations() {
      try {
        const response = await fetch(this.dataset.url, {
          headers: { Accept: "text/html" },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(
          html,
          "text/html",
        );
        const incoming = documentFragment.querySelector(
          "ion-product-recommendations",
        );

        if (incoming?.innerHTML.trim()) this.innerHTML = incoming.innerHTML;
        else this.remove();
      } catch (error) {
        console.warn(
          "[ION product] Recommendations could not be loaded.",
          error,
        );
        this.remove();
      }
    }
  }

  if (!customElements.get("ion-product-page"))
    customElements.define("ion-product-page", IonProductPage);
  if (!customElements.get("ion-product-recommendations")) {
    customElements.define(
      "ion-product-recommendations",
      IonProductRecommendations,
    );
  }
})();
